// deps
import React, { ReactElement, ReactNode, memo, useContext, useEffect, useMemo } from "react";
import classNames from "classnames";

// internal-deps
import { NewTag, useEffectOnce } from "@swiggy-private/core-ui";
import { isEmpty } from "@swiggy-private/js-utils";
import { CroutonPriority, CroutonIds } from "@swiggy-private/cart-types";

// style components
import { DownArrowIcon } from "./DeliverySpeedCrouton.style";

// styles
import styles from "./DeliverySpeedCrouton.scss";

import { CartOverlaysContext } from "@swiggy-private/checkout-core";

export interface DeliverySpeedCroutonProps {
    croutonText: string;
    croutonSubtext?: string;
    croutonIcon?: string;
    setCroutonVisibility: (croutonVisibility: boolean) => void;
    onCroutonLoad: VoidFunction;
    onClick: VoidFunction;
    showNewTag?: boolean;
}

const DeliverySpeedCrouton = ({
    croutonText,
    croutonSubtext,
    croutonIcon,
    setCroutonVisibility,
    showNewTag,
    onClick,
    onCroutonLoad,
}: DeliverySpeedCroutonProps): ReactElement => {
    const { registerCrouton } = useContext(CartOverlaysContext);

    const preTextEl: ReactNode | undefined = useMemo(() => {
        return showNewTag ? <NewTag /> : undefined;
    }, [showNewTag]);

    const postTextElClassName = classNames({
        [styles.croutonPostTextEl]: !isEmpty(croutonSubtext),
    });

    const postTextEl = useMemo(() => {
        return (
            <span className={postTextElClassName}>
                <DownArrowIcon />
            </span>
        );
    }, [postTextElClassName]);

    useEffectOnce(() => onCroutonLoad());

    const titleClassName = classNames(styles.croutonText, {
        [styles.croutonTextWithSubText]: !isEmpty(croutonSubtext),
    });

    const buttonClassName = classNames({
        [styles.croutonButton]: !isEmpty(croutonSubtext),
    });

    const croutonContent = useMemo(
        () => ({
            title: croutonText,
            subText: croutonSubtext,
            icon: croutonIcon,
            setCroutonVisibility: setCroutonVisibility,
            preTextEl: preTextEl,
            postTextEl: postTextEl,
            titleClassName: titleClassName,
            subTextClassName: styles.croutonSubText,
            iconClassName: styles.croutonIcon,
            buttonClassName: buttonClassName,
            onClick: onClick,
        }),
        [
            buttonClassName,
            croutonIcon,
            croutonSubtext,
            croutonText,
            onClick,
            postTextEl,
            preTextEl,
            setCroutonVisibility,
            titleClassName,
        ],
    );

    useEffect(() => {
        registerCrouton({
            id: CroutonIds.DELIVERY_SPEED,
            priority: CroutonPriority.PRIORITY_ORDER_ONE,
            content: croutonContent,
        });
    }, [croutonContent, registerCrouton]);

    return <></>;
};

export default memo(DeliverySpeedCrouton);
